import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Trash2, UserPlus, ShieldCheck, History, Users } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import dayjs from "dayjs";
import { useAuth } from "@/context/AuthContext";
import { prettyLoc } from "@/lib/format";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ScopeChips = ({ value, locations, onChange, size = "md", idPrefix = "scope" }) => {
  const isAll = !Array.isArray(value);
  const cls = (active) =>
    `${size === "sm" ? "px-2.5 py-1 text-[10px]" : "px-3 py-1.5 text-xs"} rounded-full font-semibold border transition-colors ${
      active ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-500 border-slate-200 hover:border-slate-400"
    }`;
  const toggle = (loc) => {
    const cur = isAll ? [] : value;
    const next = cur.includes(loc) ? cur.filter((l) => l !== loc) : [...cur, loc];
    if (next.length === 0) onChange("all");
    else onChange(next);
  };
  return (
    <div className="flex flex-wrap gap-1.5">
      <button type="button" className={cls(isAll)} onClick={() => onChange("all")} data-testid={`${idPrefix}-all`}>
        All
      </button>
      {locations.map((l) => (
        <button
          key={l}
          type="button"
          className={cls(!isAll && value.includes(l))}
          onClick={() => toggle(l)}
          data-testid={`${idPrefix}-${l.split(" ")[0].toLowerCase()}`}
        >
          {prettyLoc(l)}
        </button>
      ))}
    </div>
  );
};

export const AccessPanel = ({ open, onOpenChange }) => {
  const { user } = useAuth();
  const [list, setList] = useState([]);
  const [locations, setLocations] = useState([]);
  const [email, setEmail] = useState("");
  const [locs, setLocs] = useState("all");
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState("access");
  const [audit, setAudit] = useState([]);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/whitelist`);
      setList(res.data);
    } catch {
      toast.error("Could not load whitelist");
    }
  }, []);

  useEffect(() => {
    if (open) {
      setTab("access");
      load();
      axios.get(`${API}/locations`).then((r) => setLocations(r.data)).catch(() => {});
      axios.get(`${API}/audit`).then((r) => setAudit(r.data)).catch(() => {});
    }
  }, [open, load]);

  const add = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setBusy(true);
    try {
      await axios.post(`${API}/whitelist`, { email: email.trim(), allowed_locations: locs });
      toast.success(`${email.trim()} whitelisted`);
      setEmail("");
      setLocs("all");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not add email");
    }
    setBusy(false);
  };

  const changeAccess = async (em, next) => {
    try {
      await axios.post(`${API}/whitelist`, { email: em, allowed_locations: next });
      toast.success(`Access updated for ${em}`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update access");
    }
  };

  const remove = async (em) => {
    try {
      await axios.delete(`${API}/whitelist/${encodeURIComponent(em)}`);
      toast.success(`${em} removed`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not remove email");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="access-panel">
        <DialogHeader>
          <DialogTitle className="font-display">Dashboard Access</DialogTitle>
          <DialogDescription>Only these email IDs can sign in, limited to the locations selected for them.</DialogDescription>
        </DialogHeader>
        <div className="flex gap-1 bg-slate-100 rounded-full p-1 w-fit">
          {[
            { id: "access", label: "Access", icon: Users },
            { id: "audit", label: "Sign-in Log", icon: History },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`panel-tab-${t.id}`}
              className={`flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-semibold transition-colors ${
                tab === t.id ? "bg-white shadow-sm text-slate-900" : "text-slate-500 hover:text-slate-700"
              }`}
            >
              <t.icon size={12} /> {t.label}
            </button>
          ))}
        </div>
        {tab === "audit" ? (
          <div className="max-h-80 overflow-y-auto divide-y divide-slate-100" data-testid="audit-list">
            {audit.map((a, i) => (
              <div key={`${a.email}-${a.at}-${i}`} className="flex items-center justify-between gap-2 py-2.5 text-sm" data-testid="audit-row">
                <span className="min-w-0">
                  <span className="block truncate text-slate-700">{a.email}</span>
                  {a.name && <span className="block text-[10px] text-slate-400 truncate">{a.name}</span>}
                </span>
                <span className="shrink-0 text-xs text-slate-500 tabular-nums">{dayjs(a.at).format("DD MMM YY, HH:mm")}</span>
              </div>
            ))}
            {audit.length === 0 && <p className="text-xs text-slate-400 py-6 text-center">No sign-ins recorded yet.</p>}
          </div>
        ) : (
        <>
        <form onSubmit={add} className="space-y-2.5">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="person@company.com"
            data-testid="whitelist-email-input"
            className="w-full bg-white border border-slate-200 text-sm rounded-full px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#D4AF37]"
          />
          <div className="flex items-center justify-between gap-2">
            <ScopeChips value={locs} locations={locations} onChange={setLocs} idPrefix="add-scope" />
            <button
              type="submit"
              disabled={busy}
              data-testid="whitelist-add-btn"
              className="shrink-0 inline-flex items-center gap-1.5 rounded-full bg-slate-900 text-white text-xs font-semibold px-4 py-2.5 hover:bg-[#B08D26] transition-colors disabled:opacity-50"
            >
              <UserPlus size={13} /> Add
            </button>
          </div>
        </form>
        <div className="max-h-72 overflow-y-auto divide-y divide-slate-100" data-testid="whitelist-list">
          {list.map((w) => (
            <div key={w.email} className="py-2.5 space-y-1.5" data-testid="whitelist-row">
              <div className="flex items-center justify-between gap-2 text-sm">
                <span className="flex items-center gap-2 text-slate-700 min-w-0">
                  {w.is_admin && <ShieldCheck size={14} className="shrink-0 text-[#B08D26]" />}
                  <span className="truncate">{w.email}</span>
                </span>
                <span className="flex items-center gap-2 shrink-0">
                  {w.is_admin && <span className="text-[10px] uppercase tracking-wide font-bold text-[#B08D26]">All (Admin)</span>}
                  {w.email !== user?.email && (
                    <button
                      onClick={() => remove(w.email)}
                      data-testid={`whitelist-remove-${w.email}`}
                      className="text-slate-400 hover:text-red-500 transition-colors"
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                </span>
              </div>
              {!w.is_admin && (
                <div data-testid={`whitelist-access-${w.email}`}>
                  <ScopeChips
                    value={w.allowed_locations}
                    locations={locations}
                    onChange={(next) => changeAccess(w.email, next)}
                    size="sm"
                    idPrefix={`row-scope-${w.email}`}
                  />
                </div>
              )}
            </div>
          ))}
          {list.length === 0 && <p className="text-xs text-slate-400 py-4 text-center">No emails yet.</p>}
        </div>
        </>
        )}
      </DialogContent>
    </Dialog>
  );
};
